import React, { useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Download, FileArchive, FileImage, FileSpreadsheet, FileText, Loader2, Paperclip, X } from 'lucide-react';
import { klarysUrl } from './klarysApi';

const MAX_FICHIERS = 10;
const MAX_TAILLE = 25 * 1024 * 1024;
export const ACCEPT = '.pdf,.docx,.doc,.xlsx,.xlsm,.xls,.zip,.jpg,.jpeg,.png,.webp,.gif,.heic,.heif,.bmp,.tif,.tiff,.txt,.csv,.md,.json,.xml,.html';

const ext = (nom) => (nom.includes('.') ? nom.split('.').pop().toLowerCase() : '');
const taille = (o) => (o > 1048576 ? `${(o / 1048576).toFixed(1)} Mo` : `${Math.max(1, Math.round(o / 1024))} Ko`);
const Icone = ({ nom, className }) => {
  const e = ext(nom);
  const I = e === 'zip' ? FileArchive : ['xlsx', 'xlsm', 'xls', 'csv'].includes(e) ? FileSpreadsheet : ['pdf', 'docx', 'doc', 'txt', 'md'].includes(e) || !e ? FileText : FileImage;
  return <I className={className} />;
};

export const telechargerFichier = async (f, headers) => {
  try {
    const r = await axios.get(klarysUrl(`/fichiers/${f.id}`), { headers, responseType: 'blob' });
    const url = URL.createObjectURL(r.data);
    const a = Object.assign(document.createElement('a'), { href: url, download: f.nom });
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  } catch (e) { toast.error('Téléchargement impossible'); }
};

export const telechargerExemples = async (headers) => {
  try {
    const r = await axios.get(klarysUrl('/exemples-fichiers'), { headers, responseType: 'blob' });
    const url = URL.createObjectURL(r.data);
    Object.assign(document.createElement('a'), { href: url, download: 'Klarys_exemples_fichiers.zip' }).click();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  } catch (e) { toast.error('Téléchargement des exemples impossible'); }
};

export const fichiersColles = (e) => {
  const fichiers = Array.from(e.clipboardData?.files || []);
  if (!fichiers.length) return null;
  e.preventDefault();
  const h = new Date().toTimeString().slice(0, 8).replace(/:/g, '-');
  return fichiers.map((f, i) => (f.type.startsWith('image/') && /^image\.\w+$/i.test(f.name)
    ? new File([f], `capture-${h}${i ? `-${i}` : ''}.${f.type.split('/')[1] || 'png'}`, { type: f.type }) : f));
};

export const usePiecesJointes = (headers) => {
  const [pieces, setPieces] = useState([]);
  const maj = (cle, v) => setPieces((p) => p.map((x) => (x.cle === cle ? { ...x, ...v } : x)));
  const ajouter = async (liste, convId) => {
    const fichiers = Array.from(liste || []);
    if (pieces.length + fichiers.length > MAX_FICHIERS) { toast.error(`${MAX_FICHIERS} fichiers maximum par envoi`); return; }
    const valides = fichiers.filter((f) => {
      if (f.size > MAX_TAILLE) { toast.error(`« ${f.name} » dépasse 25 Mo`); return false; }
      if (!ACCEPT.split(',').includes(`.${ext(f.name)}`)) { toast.error(`« ${f.name} » : format non pris en charge`); return false; }
      return true;
    });
    if (!valides.length) return;
    const id = await convId();
    const nouvelles = valides.map((f) => ({ cle: `${f.name}-${f.size}-${Math.random()}`, nom: f.name, taille: f.size, statut: 'envoi', progres: 0, f }));
    setPieces((p) => [...p, ...nouvelles]);
    await Promise.all(nouvelles.map(async (n) => {
      const fd = new FormData();
      fd.append('fichier', n.f);
      try {
        const r = await axios.post(klarysUrl(`/conversations/${id}/fichiers`), fd, {
          headers, onUploadProgress: (e) => maj(n.cle, { progres: e.total ? Math.round((e.loaded / e.total) * 100) : 0 }),
        });
        maj(n.cle, { statut: 'pret', id: r.data.id, conv: id, info: r.data });
      } catch (e) {
        maj(n.cle, { statut: 'erreur' });
        toast.error(e.response?.data?.detail || `« ${n.nom} » n'a pas pu être envoyé`);
      }
    }));
  };
  return {
    pieces, ajouter,
    retirer: (cle) => {
      const p = pieces.find((x) => x.cle === cle);
      if (p?.id) axios.delete(klarysUrl(`/fichiers/${p.id}`), { headers }).catch(() => {});
      setPieces((l) => l.filter((x) => x.cle !== cle));
    },
    vider: () => setPieces([]),
    prets: () => pieces.filter((x) => x.statut === 'pret').map((x) => ({ id: x.id, nom: x.nom, taille: x.taille, conv: x.conv })),
    enCours: pieces.some((x) => x.statut === 'envoi'),
  };
};

export const BoutonJoindre = ({ onFichiers, disabled }) => {
  const input = useRef(null);
  return (
    <>
      <input ref={input} type="file" multiple accept={ACCEPT} className="hidden" data-testid="klarys-fichier-input"
        onChange={(e) => { onFichiers(e.target.files); e.target.value = ''; }} />
      <button type="button" onClick={() => input.current?.click()} disabled={disabled} aria-label="Joindre un fichier"
        title="Joindre un fichier : PDF, Word, texte, JPG/PNG/WEBP, Excel, CSV, ZIP — 10 fichiers, 25 Mo chacun. Glisser-déposer ou Ctrl+V d'une capture possibles." data-testid="klarys-joindre-btn"
        className="inline-flex h-11 shrink-0 items-center justify-center gap-1.5 rounded-md border border-slate-300 px-3 text-xs font-semibold text-slate-700 transition-colors hover:border-[#0a3d62] hover:bg-sky-50 hover:text-[#0a3d62] disabled:opacity-50">
        <Paperclip className="h-4 w-4" /><span className="hidden whitespace-nowrap sm:inline">Joindre un fichier</span>
      </button>
    </>
  );
};

export const KlarysPiecesEnAttente = ({ pj }) => (pj.pieces.length ? (
  <div className="mb-2 flex flex-wrap gap-1.5" data-testid="klarys-pieces-attente">
    {pj.pieces.map((p, i) => (
      <span key={p.cle} data-testid={`klarys-piece-${i}`}
        className={`inline-flex max-w-[240px] items-center gap-1.5 rounded-md border px-2 py-1 text-[11px] ${p.statut === 'erreur' ? 'border-rose-200 bg-rose-50 text-rose-700' : 'border-slate-200 bg-slate-50 text-slate-700'}`}>
        {p.statut === 'envoi' ? <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-[#0a3d62]" /> : <Icone nom={p.nom} className="h-3.5 w-3.5 shrink-0" />}
        <span className="truncate">{p.nom}</span>
        <span className="shrink-0 text-slate-400" data-testid={`klarys-piece-${i}-statut`}>
          {p.statut === 'envoi' ? (p.progres < 100 ? `${p.progres} %` : 'analyse…') : p.statut === 'erreur' ? 'échec' : taille(p.taille)}
        </span>
        <button type="button" onClick={() => pj.retirer(p.cle)} aria-label="Retirer" data-testid={`klarys-piece-${i}-retirer`} className="shrink-0 hover:text-rose-600">
          <X className="h-3 w-3" />
        </button>
      </span>
    ))}
  </div>
) : null);

export const KlarysPiecesMessage = ({ fichiers, headers, testId }) => (
  <div className="flex flex-wrap justify-end gap-1.5 pr-9" data-testid={testId}>
    {fichiers.map((f, i) => (
      <button key={f.id} type="button" onClick={() => telechargerFichier(f, headers)} data-testid={`${testId}-${i}`} title="Télécharger"
        className="inline-flex max-w-[240px] items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2 py-1 text-[11px] text-slate-700 hover:border-[#0a3d62]">
        <Icone nom={f.nom} className="h-3.5 w-3.5 shrink-0 text-[#0a3d62]" /> <span className="truncate">{f.nom}</span>
      </button>
    ))}
  </div>
);

export const KlarysDocumentCard = ({ f, headers, testId }) => (
  <div className="ml-9 flex max-w-md items-center gap-3 rounded-xl border border-emerald-200 bg-emerald-50/60 px-3 py-2.5" data-testid={testId}>
    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white text-emerald-700 shadow-sm"><Icone nom={f.nom} className="h-5 w-5" /></span>
    <div className="min-w-0 flex-1">
      <p className="truncate text-sm font-semibold text-slate-800" data-testid={`${testId}-nom`}>{f.nom}</p>
      <p className="text-[11px] text-slate-500">{(f.format || ext(f.nom)).toUpperCase()} · {taille(f.taille || 0)} · classé dans la GED</p>
    </div>
    <button type="button" onClick={() => telechargerFichier(f, headers)} data-testid={`${testId}-telecharger`}
      className="inline-flex items-center gap-1 rounded-md bg-[#0a3d62] px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-[#0c4a77]">
      <Download className="h-3.5 w-3.5" /> Télécharger
    </button>
  </div>
);
